'use client';

import { useState } from 'react';
export type NetlifyStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Netlify Forms submit hook for React (App Router) forms.
 *
 * Netlify registers the form schemas from the static public/__forms.html stub
 * at build time; the live React form posts here as a url-encoded AJAX request
 * to /__forms.html — the pattern required by @netlify/plugin-nextjs.
 * The body MUST include `form-name` and the `bot-field` honeypot.
 */
export function useNetlifyForm(onSuccess?: () => void) {
  const [status, setStatus] = useState<NetlifyStatus>('idle');
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus('submitting');
    const body = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      body.append(key, typeof value === 'string' ? value : value.name);
    });
    const succeed = () => {
      form.reset(); // clears uncontrolled inputs
      onSuccess?.(); // let the form clear its React-controlled state too
      setStatus('success');
    };
    try {
      const response = await fetch('/__forms.html', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });
      if (!response.ok) {
        // The /__forms.html endpoint only exists on Netlify's infra; in local
        // dev it 404s. Preview the success state so the UX is testable.
        if (process.env.NODE_ENV !== 'production') {
          succeed();
          return;
        }
        throw new Error(`Netlify form POST returned ${response.status}`);
      }
      succeed();
    } catch {
      setStatus('error');
    }
  };
  return {
    status,
    handleSubmit
  };
}
const MESSAGES: Record<string, {
  success: string;
  error: string;
  submitting: string;
}> = {
  fr: {
    success: 'Merci ! Votre demande a bien été envoyée.',
    error: 'Une erreur est survenue. Réessayez ou appelez-nous.',
    submitting: 'Envoi…'
  },
  en: {
    success: 'Thanks! Your request has been sent.',
    error: 'Something went wrong. Please retry or call us.',
    submitting: 'Sending…'
  },
  nl: {
    success: 'Bedankt! Uw aanvraag is verzonden.',
    error: 'Er ging iets mis. Probeer opnieuw of bel ons.',
    submitting: 'Verzenden…'
  },
  es: {
    success: '¡Gracias! Su solicitud ha sido enviada.',
    error: 'Algo salió mal. Inténtelo de nuevo o llámenos.',
    submitting: 'Enviando…'
  },
  it: {
    success: 'Grazie! La tua richiesta è stata inviata.',
    error: 'Si è verificato un errore. Riprova o chiamaci.',
    submitting: 'Invio…'
  }
};
export function netlifyFormMessage(locale: string, status: NetlifyStatus): string {
  const m = MESSAGES[locale] || MESSAGES.en;
  if (status === 'success') return m.success;
  if (status === 'error') return m.error;
  if (status === 'submitting') return m.submitting;
  return '';
}